import React from 'react';
import { CallInsightsAnalysis } from '../../adherence/types';
import type { SalesPlaybookUsage } from '../PlaybookUsagePanel';
import { SummaryAdminContext } from './SummarySectionCard';
import ToneSummaryCard from './ToneSummaryCard';
import SpeedSummaryCard from './SpeedSummaryCard';
import TalkPercentSummaryCard from './TalkPercentSummaryCard';
import IntroSummaryCard from './IntroSummaryCard';
import QualifyingQuestionsSummaryCard from './QualifyingQuestionsSummaryCard';
import ObjectionHandlingSummaryCard from './ObjectionHandlingSummaryCard';
import FaqHandlingSummaryCard from './FaqHandlingSummaryCard';
import LandmineSummaryCard from './LandmineSummaryCard';
import CompetitorSummaryCard from './CompetitorSummaryCard';
import AdvanceDealSummaryCard from './AdvanceDealSummaryCard';
import NextStepsSummaryCard from './NextStepsSummaryCard';
import ParticipantsSummaryCard from './ParticipantsSummaryCard';
import { hasParticipants } from '../multiPartyFormat';
import OverviewBulletsPanel from '../../ui/OverviewBulletsPanel';

interface SpeechRateAlert {
  total_words: number;
  words_per_minute: number;
  timestamp: string;
}

export interface CallSummaryTabProps {
  sessionId: string;
  callInsights: CallInsightsAnalysis;
  salesPlaybookUsage?: SalesPlaybookUsage;
  speechRateAlerts: SpeechRateAlert[];
  isAdmin: boolean;
  onAnalysisUpdated: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
  onSwitchToDetails: (section: DetailsSection, subSection?: string) => void;
}

export type DetailsSection = 'participants' | 'sales_intelligence' | 'knowledge_base' | 'playbook';

const LEVEL_LABELS = ['Delivery', 'Opening', 'Knowledge', 'Deal', 'Next Focus'] as const;

/** Vertical arrow connector between flowchart levels. */
const FlowConnector: React.FC = () => (
  <div className="flex justify-center items-center py-1 pointer-events-none select-none text-ink-disabled">
    <svg
      width="24"
      height="64"
      viewBox="0 0 24 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <line x1="12" y1="0" x2="12" y2="50" stroke="currentColor" strokeWidth="2" strokeDasharray="5 3" />
      <path
        d="M5 46L12 58L19 46"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const CallSummaryTab: React.FC<CallSummaryTabProps> = ({
  sessionId,
  callInsights,
  speechRateAlerts,
  isAdmin,
  onNavigateToTimestamp,
  onSwitchToDetails,
}) => {
  const intelligence = callInsights?.sales_intelligence;
  const playbook = callInsights?.playbook;
  const objFaqItems = callInsights?.objection_faq?.items ?? [];
  const landmineItems = callInsights?.landmine?.items ?? [];
  const competitorItems = callInsights?.competitor?.items ?? [];
  const toneAnalysis = callInsights?.tone_analysis;

  // Extract phase timestamps for timeline navigation
  const openerPhase = intelligence?.phases?.find((p) => p.phase === 'opener');
  const discoveryPhase = intelligence?.phases?.find((p) => p.phase === 'discovery');
  const closeAttemptPhase = intelligence?.phases?.find((p) => p.phase === 'close_attempt');

  const firstAlertTs = speechRateAlerts?.[0]?.timestamp;

  const summaryBullets = callInsights?.call_summary_bullets;

  return (
    <SummaryAdminContext.Provider value={{
      isAdmin,
      sessionId,
      coachingOverrides: callInsights?.coaching_overrides ?? {},
    }}>
      <div className="space-y-0">

        {/* ── Top panels: one group, so the column's space-y-0 cannot zero their spacing ── */}
        <div className="space-y-4 mb-4">
          {/* Who was on the call: the same card for a 1-to-1 call and a meeting */}
          {hasParticipants(callInsights?.participants) && callInsights?.participants && (
            <ParticipantsSummaryCard
              data={callInsights.participants}
              onViewDetails={() => onSwitchToDetails('participants')}
            />
          )}

          {/* Call Overview panel (only when LLM bullets are available) */}
          {summaryBullets && summaryBullets.length > 0 && (
            <OverviewBulletsPanel title="Call Overview" bullets={summaryBullets} labels={LEVEL_LABELS} />
          )}
        </div>

        {/* ── Level 1: Rep delivery basics ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <ToneSummaryCard
            toneAnalysis={toneAnalysis}
            onViewDetails={() => onSwitchToDetails('sales_intelligence')}
          />
          <SpeedSummaryCard
            speechRateAlerts={speechRateAlerts}
            speechRateAssessment={intelligence?.speech_rate_assessment}
            onViewInTimeline={firstAlertTs ? () => onNavigateToTimestamp?.(firstAlertTs) : undefined}
          />
          <TalkPercentSummaryCard
            speakerStats={callInsights?.speaker_stats}
          />
        </div>

        <FlowConnector />

        {/* ── Level 2: Opening & discovery ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <IntroSummaryCard
            playbook={playbook}
            openerPhaseTimestamp={openerPhase?.primary_timestamp}
            onViewDetails={() => onSwitchToDetails('playbook')}
            onViewInTimeline={
              openerPhase?.primary_timestamp
                ? () => onNavigateToTimestamp?.(openerPhase.primary_timestamp!)
                : undefined
            }
          />
          <QualifyingQuestionsSummaryCard
            playbook={playbook}
            discoveryPhaseTimestamp={discoveryPhase?.primary_timestamp}
            onViewDetails={() => onSwitchToDetails('playbook')}
            onViewInTimeline={
              discoveryPhase?.primary_timestamp
                ? () => onNavigateToTimestamp?.(discoveryPhase.primary_timestamp!)
                : undefined
            }
          />
        </div>

        <FlowConnector />

        {/* ── Level 3: Knowledge base handling ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
          <ObjectionHandlingSummaryCard
            items={objFaqItems}
            overallSummary={callInsights?.objection_faq?.overall_summary}
            onViewDetails={() => onSwitchToDetails('knowledge_base', 'objection_faq')}
            onViewInTimeline={(() => {
              const worst = [...objFaqItems]
                .filter((i) => i.category === 'objection')
                .sort((a, b) => a.handling_score - b.handling_score)[0];
              return worst?.timestamp_iso
                ? () => onNavigateToTimestamp?.(worst.timestamp_iso!)
                : undefined;
            })()}
          />
          <FaqHandlingSummaryCard
            items={objFaqItems}
            overallSummary={callInsights?.objection_faq?.overall_summary}
            onViewDetails={() => onSwitchToDetails('knowledge_base', 'objection_faq')}
            onViewInTimeline={(() => {
              const worst = [...objFaqItems]
                .filter((i) => i.category === 'faq')
                .sort((a, b) => a.handling_score - b.handling_score)[0];
              return worst?.timestamp_iso
                ? () => onNavigateToTimestamp?.(worst.timestamp_iso!)
                : undefined;
            })()}
          />
          <LandmineSummaryCard
            items={landmineItems}
            overallSummary={callInsights?.landmine?.overall_summary}
            onViewDetails={() => onSwitchToDetails('knowledge_base', 'landmine')}
            onViewInTimeline={(() => {
              const highTs =
                landmineItems.find((i) => i.priority?.toLowerCase() === 'high')?.timestamp_iso ??
                landmineItems[0]?.timestamp_iso;
              return highTs ? () => onNavigateToTimestamp?.(highTs) : undefined;
            })()}
          />
          <CompetitorSummaryCard
            items={competitorItems}
            overallSummary={callInsights?.competitor?.overall_summary}
            onViewDetails={() => onSwitchToDetails('knowledge_base', 'competitor')}
            onViewInTimeline={
              competitorItems[0]?.timestamp_iso
                ? () => onNavigateToTimestamp?.(competitorItems[0].timestamp_iso!)
                : undefined
            }
          />
        </div>

        <FlowConnector />

        {/* ── Level 4: Deal advancement (full-width) ── */}
        <AdvanceDealSummaryCard
          playbook={playbook}
          salesIntelligence={intelligence}
          closeAttemptTimestamp={closeAttemptPhase?.primary_timestamp}
          onViewDetails={() => onSwitchToDetails('playbook')}
          onViewInTimeline={
            closeAttemptPhase?.primary_timestamp
              ? () => onNavigateToTimestamp?.(closeAttemptPhase.primary_timestamp!)
              : undefined
          }
        />

        <FlowConnector />

        {/* ── Level 5: Next steps (full-width) ── */}
        <NextStepsSummaryCard
          coachingForThisClient={intelligence?.coaching_for_this_client}
          coachingForGeneralImprovement={intelligence?.coaching_for_general_improvement}
          onViewDetails={() => onSwitchToDetails('sales_intelligence')}
        />
      </div>
    </SummaryAdminContext.Provider>
  );
};

export default CallSummaryTab;
